import React, { useState } from 'react';
import { FaGithub, FaLinkedin, FaRocket, FaCheckCircle, FaStar } from 'react-icons/fa';
import { MdEmail, MdCode, MdLayers, MdDashboard, MdMessage, MdClose, MdSpeed } from 'react-icons/md';
import { FiChevronRight, FiExternalLink, FiArrowUpRight } from 'react-icons/fi';

export default function App() {
  const [selectedProject, setSelectedProject] = useState(null);

  const projectsData = [
    {
      id: "stanley",
      title: "Stanley Café - Interactive Brand Experience",
      shortDesc: "A high-converting, ultra-fast web showcase for a specialty coffee shop with dynamic menus.",
      fullDesc: "Stanley Café needed an online presence that mirrors their premium physical atmosphere while driving walk-in customers and takeaway orders.",
      img: "/stanleycafe.png",
      demo: "https://stanley-cafe.vercel.app/",
      github: "https://github.com/mostafa-1010ashraf0101/stanley-cafe",
      tags: ["React.js", "Tailwind CSS", "UI/UX Architecture", "Vercel"],
      challenge: "Slow mobile loading times on traditional WordPress platforms were causing high bounce rates among customers trying to access the menu via QR codes inside the café.",
      solution: "Engineered a lightweight, static-first Single Page Application using React and Tailwind CSS. Optimized asset loading and layout shifts to achieve instant renders.",
      impact: "Reduced menu loading time by 85%, achieving a 99/100 Mobile Lighthouse score and smooth 60fps scrolling interactions."
    },
    {
      id: "zidan",
      title: "E-Store - Scalable E-Commerce Engine",
      shortDesc: "A modern e-commerce platform designed for fast product discovery, filtering, and seamless cart operations.",
      fullDesc: "A complete digital storefront built to handle multi-category apparel catalog with real-time cart states and responsive checkout flows.",
      img: "/zidan.png",
      demo: "https://zidan-store.vercel.app/",
      github: "https://github.com/mostafa-1010ashraf0101/zidan",
      tags: ["React", "State Management", "Tailwind CSS", "REST Integration"],
      challenge: "Managing complex shopping cart states across different viewports while maintaining quick interaction feedback without page reloads.",
      solution: "Implemented an optimized React state architecture to handle cart mutations, variant filtering, and price calculations in real-time.",
      impact: "Delivered a zero-latency shopping experience with immediate UI feedback on add-to-cart actions."
    },
    {
      id: "sneakerhub",
      title: "SneakerHub - Analytics & Admin Control Center",
      shortDesc: "Data-driven administrative dashboard featuring live metrics, interactive data visualizers, and order management.",
      fullDesc: "An enterprise-grade admin console for e-commerce store owners to track sales metrics, inventory movements, and customer behavior.",
      img: "/sneakerhub-dashboard.png", 
      demo: "https://sneakerhub-dashboard.vercel.app",
      github: "https://github.com/mostafa-1010ashraf0101/dashboard",
      tags: ["React", "Recharts", "Dashboard Design", "Dark Mode UI"],
      challenge: "Displaying heavy, multi-dimensional sales data without overwhelming the admin user interface or slowing down browser rendering.",
      solution: "Integrated modular Recharts components with customizable data views, custom search filters, and an accessible dark-theme aesthetic.",
      impact: "Streamlined daily store analytics, enabling store managers to assess revenue and inventory status in under 5 seconds."
    }
  ];

  return (
    <div className="min-h-screen bg-[#090d16] text-slate-100 selection:bg-blue-500/30 font-sans antialiased relative overflow-hidden">
      
      {/* Dynamic Background Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-blue-600/15 via-indigo-600/10 to-transparent blur-[120px] pointer-events-none -z-10" />
      <div className="absolute top-[30%] right-0 w-[500px] h-[500px] bg-purple-600/10 blur-[140px] pointer-events-none -z-10" />

      {/* Navigation Bar */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-[#090d16]/70 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-5 flex justify-between items-center">
          <a href="#" className="text-2xl font-black tracking-tighter text-white flex items-center gap-1 group">
            MOSTAFA<span className="text-blue-500 group-hover:scale-150 transition-transform inline-block">.</span>
          </a>
          <div className="hidden md:flex gap-8 text-sm font-medium items-center text-slate-300">
            <a href="#about" className="hover:text-blue-400 transition-colors">About</a>
            <a href="#services" className="hover:text-blue-400 transition-colors">Services</a>
            <a href="#projects" className="hover:text-blue-400 transition-colors">Portfolio</a>
            <a 
              href="#contact" 
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 font-semibold transition duration-300 border border-blue-400/30"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="max-w-6xl mx-auto px-6 pt-20 pb-32 relative">
        <div className="max-w-3xl">
          
          {/* Status Badge */}
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-slate-800/80 border border-slate-700/80 text-blue-400 text-xs font-semibold mb-8 backdrop-blur-md shadow-inner">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
            <span>Available for International Remote & Contract Work</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-8 leading-[1.1] tracking-tight">
            Crafting Scalable & <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400">
              High-Impact Frontend
            </span> Products.
          </h1>

          <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed font-normal">
            Specialized Frontend Developer focused on building ultra-fast React applications, pixel-perfect user interfaces, and robust web experiences tailored for global growth.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-5">
            <a 
              href="#projects" 
              className="px-8 py-4 bg-white text-slate-900 font-bold rounded-xl hover:bg-blue-500 hover:text-white transition-all duration-300 flex items-center gap-2 shadow-xl shadow-white/5 hover:shadow-blue-500/20 hover:-translate-y-0.5"
            >
              Explore Selected Work <FiChevronRight size={20} />
            </a>
            <div className="flex items-center gap-4 px-2 text-slate-400">
              <a href="https://github.com/mostafa-1010ashraf0101" target="_blank" rel="noreferrer" className="p-3 bg-slate-800/50 hover:bg-slate-800 rounded-xl border border-slate-700/60 hover:text-white transition duration-300">
                <FaGithub size={20} />
              </a>
              <a href="https://www.linkedin.com/in/mostafa-ashraf-b29210315/" target="_blank" rel="noreferrer" className="p-3 bg-slate-800/50 hover:bg-slate-800 rounded-xl border border-slate-700/60 hover:text-white transition duration-300">
                <FaLinkedin size={20} />
              </a>
            </div>
          </div>

          {/* Performance Stats Strip */}
          <div className="mt-16 pt-8 border-t border-slate-800/80 grid grid-cols-2 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <MdSpeed className="text-blue-400" size={28} />
              <div>
                <div className="text-xl font-bold text-white">99+</div>
                <div className="text-xs text-slate-400">Lighthouse Performance</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <FaCheckCircle className="text-indigo-400" size={24} />
              <div>
                <div className="text-xl font-bold text-white">100%</div>
                <div className="text-xs text-slate-400">Clean & Testable Code</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <FaRocket className="text-purple-400" size={24} />
              <div>
                <div className="text-xl font-bold text-white">Zero</div>
                <div className="text-xs text-slate-400">Layout Shift (CLS)</div>
              </div>
            </div>
          </div>

        </div>
      </header>

      {/* About Me Section */}
      <section id="about" className="max-w-6xl mx-auto px-6 py-24 border-t border-slate-800/80">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-2 block">Engineering Philosophy</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Built for Performance, Designed for Growth.</h2>
            <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-4">
              I'm Mostafa, a Frontend Developer based in Alexandria, Egypt. I bridge the gap between design vision and technical execution.
            </p>
            <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-6">
              I specialize in architecting modern React interfaces that load instantly, scale smoothly, and drive measurable user engagement. My focus is writing maintainable code and building seamless web applications.
            </p>
            <div className="flex gap-4">
              <span className="px-3.5 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs font-medium border border-slate-700">React.js</span>
              <span className="px-3.5 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs font-medium border border-slate-700">JavaScript (ES6+)</span>
              <span className="px-3.5 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs font-medium border border-slate-700">Tailwind CSS</span>
            </div>
          </div>
          <div className="flex justify-center relative">
            <div className="absolute w-80 h-80 bg-blue-600/15 rounded-full blur-3xl -z-10"></div>
            <div className="p-2 rounded-3xl bg-gradient-to-b from-slate-700 to-slate-800/40 border border-slate-700/80 shadow-2xl">
              <img 
                src="/mostafa.jpg" 
                alt="Mostafa" 
                className="rounded-2xl w-80 h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="max-w-6xl mx-auto px-6 py-24 border-t border-slate-800/80">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-2 block">Capabilities</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white">How I Add Value to Your Team</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <ServiceCard 
            icon={<MdCode className="text-blue-400" size={32} />} 
            title="Frontend Application Dev" 
            desc="Building robust Single Page Applications (SPAs) with clean React architectures, efficient state management, and reusable modular code." 
          />
          <ServiceCard 
            icon={<MdLayers className="text-indigo-400" size={32} />} 
            title="Design System & UI/UX" 
            desc="Translating Figma/Framer designs into pixel-perfect code with consistent typography, color tokens, and smooth micro-interactions." 
          />
          <ServiceCard 
            icon={<MdDashboard className="text-purple-400" size={32} />} 
            title="Dashboards & Data Views" 
            desc="Engineering complex admin portals with interactive charts, dynamic data filtering, and responsive dark-mode layouts." 
          />
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="max-w-6xl mx-auto px-6 py-24 border-t border-slate-800/80">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-2 block">Selected Work</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">Featured Case Studies</h2>
          </div>
          <p className="text-slate-400 text-sm max-w-md">
            Click on any project card to review the full technical case study, challenge breakdown, and engineering solution.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {projectsData.map((project) => (
            <div 
              key={project.id}
              onClick={() => setSelectedProject(project)}
              className="bg-slate-900/50 border border-slate-800/80 rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1.5 cursor-pointer flex flex-col justify-between group shadow-lg"
            >
              <div>
                <div className="relative overflow-hidden h-52 border-b border-slate-800/60">
                  <img src={project.img} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg shadow-lg flex items-center gap-1.5">
                      View Case Study <FiArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">{project.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5 line-clamp-2">{project.shortDesc}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-2">
                    {project.tags.map((tag, idx) => (
                      <span key={idx} className="text-[11px] px-2.5 py-1 rounded-md bg-slate-800 text-slate-300 font-medium border border-slate-700/60">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 pt-0 flex justify-between items-center border-t border-slate-800/40 mt-4 text-xs font-semibold text-blue-400">
                <span>Inspect Technical Breakdown</span>
                <FiChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Case Study Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
          <div className="bg-[#0f172a] border border-slate-700/80 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 relative shadow-2xl">
            
            <button 
              onClick={() => setSelectedProject(null)}
              className="absolute top-6 right-6 p-2 rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition"
            >
              <MdClose size={20} />
            </button>

            <span className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-2 block">Case Study</span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">{selectedProject.title}</h2>
            
            <div className="flex flex-wrap gap-2 mb-6">
              {selectedProject.tags.map((tag, idx) => (
                <span key={idx} className="text-xs px-3 py-1 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20 font-medium">
                  {tag}
                </span>
              ))}
            </div>

            <img src={selectedProject.img} alt={selectedProject.title} className="w-full h-64 object-cover rounded-2xl border border-slate-700 mb-8" />

            <div className="space-y-6 text-slate-300 text-sm md:text-base leading-relaxed">
              <div>
                <h4 className="text-white font-bold text-lg mb-2">Project Overview</h4>
                <p className="text-slate-400">{selectedProject.fullDesc}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 bg-slate-900/60 p-6 rounded-2xl border border-slate-800">
                <div>
                  <h5 className="text-rose-400 font-bold text-sm uppercase tracking-wider mb-2">The Challenge</h5>
                  <p className="text-slate-400 text-xs md:text-sm">{selectedProject.challenge}</p>
                </div>
                <div>
                  <h5 className="text-emerald-400 font-bold text-sm uppercase tracking-wider mb-2">The Engineering Solution</h5>
                  <p className="text-slate-400 text-xs md:text-sm">{selectedProject.solution}</p>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 p-5 rounded-2xl">
                <h5 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-1">Measured Impact & Result</h5>
                <p className="text-slate-200 text-sm font-medium">{selectedProject.impact}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-8 pt-6 border-t border-slate-800">
              <a 
                href={selectedProject.demo} 
                target="_blank" 
                rel="noreferrer" 
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-sm flex items-center gap-2 transition"
              >
                Live Demo <FiExternalLink size={16} />
              </a>
              <a 
                href={selectedProject.github} 
                target="_blank" 
                rel="noreferrer" 
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl text-sm flex items-center gap-2 border border-slate-700 transition"
              >
                <FaGithub size={16} /> Source Code
              </a>
            </div>

          </div>
        </div>
      )}

      {/* Contact Section */}
      <section id="contact" className="max-w-6xl mx-auto px-6 py-28 border-t border-slate-800/80 text-center relative">
        <div className="max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-2 block">Initiate Contact</span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">Let's Build Your Next High-Performance Product.</h2>
          <p className="text-slate-400 mb-10 text-base md:text-lg">
            Currently accepting freelance contracts and remote roles. Send an email or connect directly on WhatsApp to discuss your project requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="mailto:mostafadeova@gmail.com" 
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:shadow-xl hover:shadow-blue-500/25 transition duration-300 inline-flex items-center gap-2.5 border border-blue-400/30"
            >
              <MdEmail size={20} /> Send Direct Email
            </a>
            <a 
              href="https://wa.me/201115592972" 
              target="_blank" 
              rel="noreferrer" 
              className="px-8 py-4 bg-slate-900 text-white font-bold rounded-xl border border-slate-700 hover:bg-slate-800 transition duration-300 inline-flex items-center gap-2.5"
            >
              <MdMessage size={20} /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-slate-800/80 text-center text-slate-500 text-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div>© {new Date().getFullYear()} Mostafa Ashraf. Engineered with React & Tailwind CSS.</div>
        <div className="text-xs text-slate-600">Alexandria, Egypt ➔ Worldwide Remote</div>
      </footer>

    </div>
  );
}

function ServiceCard({ icon, title, desc }) {
  return (
    <div className="p-8 bg-slate-900/40 border border-slate-800 rounded-2xl hover:border-blue-500/40 transition-all duration-300 group hover:-translate-y-1">
      <div className="mb-5 p-3 rounded-xl bg-slate-800/60 w-fit group-hover:scale-110 transition-transform">{icon}</div>
      <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}
